import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Skeleton, Tooltip } from '@patternfly/react-core';
import { AlertVariant } from '@patternfly/react-core';
import { faTimes, faExpand, faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { faGear, faSparkles, faCompass } from '@fortawesome/pro-solid-svg-icons';
import InsightsActions from './InsightsActions';
import ChatInterface, { ChatMessages, Message } from './ChatInterface';
import { InsightsActions as InsightsActionsAPI } from '../../api/lambda/InsightsActions';
import { InsightsActionsResponse } from '../../api/types';
import { InsightsActionsRequest } from '../../api/types/insights-actions-request';
import { useApplication, useIsGranted } from '../user/ApplicationProvider';
import { Permission } from '../../enums/permission.enum';
import { ZoneSettingEnum } from '../../api/zone/ZoneSetting';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { useMount } from 'react-use';
import ChartMetaDataModal from '../../helpers/helper-components/ChartMetaDataModal';
import { Chart } from '../../api/charts/Chart';
import { TChartMetaDetails } from '../../api/charts/ChartMetaDetails';
import DiscoverQuestionsModal from './DiscoverQuestionsModal';
import { Question, TQuestion } from '../../api/questions/Questions';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const retryWithBackoff = async <T,>(
	fn: () => Promise<T>,
	maxRetries = 3,
	baseDelayMs = 1000
): Promise<T> => {
	let lastError: Error | null = null;
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			return await fn();
		} catch (error) {
			lastError = error as Error;
			if (attempt < maxRetries - 1) {
				const delayMs = baseDelayMs * Math.pow(2, attempt);
				console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delayMs}ms`);
				await delay(delayMs);
			}
		}
	}
	throw lastError;
};

interface InsightsChartSidebarProps {
	name?: string;
	previewData?: { csvData?: string[] };
	insightsActionsRequest?: InsightsActionsRequest;
	chartAiMetadata?: string;
	insightsOpen?: boolean;
	setInsightsOpen?: (open: boolean) => void;
	chartId?: string;
}

const InsightsChartSidebar: React.FC<InsightsChartSidebarProps> = ({
	name,
	previewData,
	insightsActionsRequest,
	chartAiMetadata,
	insightsOpen,
	setInsightsOpen,
	chartId,
}) => {
	const { addToast } = useToast();
	const isGranted = useIsGranted();
	const hasViewQuestionPerm = isGranted(Permission.ViewQuestion);
	const { zoneSettings } = useApplication();

	// Insights-disabled zone check
	const [isInsightsDisabled, setIsInsightsDisabled] = useState(false);

	useMount(() => {
		const disableZoneSetting = zoneSettings.find(
			(x) => x.zone_setting_enum_id == ZoneSettingEnum.AI_disabled
		);
		if (disableZoneSetting && disableZoneSetting.enabled) {
			setIsInsightsDisabled(true);
		}
	});

	// Insights data state
	const [insightsData, setInsightsData] = useState<InsightsActionsResponse | undefined>();
	const [isInsightsLoading, setIsInsightsLoading] = useState(false);
	const [insightsError, setInsightsError] = useState(false);
	const [hasAutoGenerated, setHasAutoGenerated] = useState(false);

	// Chat state
	const [chatMessages, setChatMessages] = useState<Message[]>([]);
	const [isChatThinking, setIsChatThinking] = useState(false);
	const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);
	const [hasInteracted, setHasInteracted] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);

	// Chart meta details
	const [chartMetaDetails, setChartMetaDetails] = useState(chartAiMetadata);
	const [isChartMetaDetailsModalOpen, setIsChartMetaDetailsModalOpen] = useState(false);

	// Discover questions
	const [isDiscoverQuestionsModalOpen, setIsDiscoverQuestionsModalOpen] = useState(false);
	const [_fetchedQuestions, setFetchedQuestions] = useState<TQuestion[]>([]);

	// Refs
	const sidebarContainerRef = useRef<HTMLDivElement | null>(null);
	const insightsThreadRef = useRef<HTMLDivElement | null>(null);
	const isNearBottomRef = useRef(true);

	// Dynamic sidebar height — fires on mount, no setTimeout needed
	useEffect(() => {
		const sidebarEl = sidebarContainerRef.current;
		if (!sidebarEl) return;

		const panelEl = sidebarEl.closest('.right-sidebar-panel') as HTMLElement;
		if (!panelEl) return;

		const scrollContainer = panelEl.closest('.pf-c-page__main') || document.documentElement;

		let rafId: number | null = null;

		const updateHeight = () => {
			const panelRect = panelEl.getBoundingClientRect();
			const footer = document.querySelector('.ribbon-footer');
			const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight;

			const bottom = Math.min(window.innerHeight, footerTop);
			const newHeight = Math.floor(Math.max(bottom - panelRect.top, 200));

			const currentHeight = parseInt(panelEl.style.height || '0', 10);
			if (Math.abs(newHeight - currentHeight) > 1) {
				panelEl.style.height = `${newHeight}px`;
				panelEl.style.maxHeight = `${newHeight}px`;
			}
		};

		const onScrollOrResize = () => {
			if (rafId !== null) return;
			rafId = requestAnimationFrame(() => {
				updateHeight();
				rafId = null;
			});
		};

		scrollContainer.addEventListener('scroll', onScrollOrResize, { passive: true });
		window.addEventListener('resize', onScrollOrResize, { passive: true });

		const subheader = document.querySelector('.sub-nav');
		let observer: ResizeObserver | null = null;
		if (subheader) {
			observer = new ResizeObserver(onScrollOrResize);
			observer.observe(subheader);
		}

		updateHeight();

		return () => {
			scrollContainer.removeEventListener('scroll', onScrollOrResize);
			window.removeEventListener('resize', onScrollOrResize);
			observer?.disconnect();
			if (rafId !== null) cancelAnimationFrame(rafId);
			panelEl.style.height = '';
			panelEl.style.maxHeight = '';
		};
	}, []);

	// Track whether the user is scrolled near the bottom of the insights thread
	useEffect(() => {
		const container = insightsThreadRef.current;
		if (!container) return;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;
			isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 100;
		};

		container.addEventListener('scroll', handleScroll);
		return () => container.removeEventListener('scroll', handleScroll);
	}, []);

	// Auto-scroll when near bottom or when the user just sent a message
	const scrollInsightsThread = () => {
		if (insightsThreadRef.current) {
			insightsThreadRef.current.scrollTop = insightsThreadRef.current.scrollHeight;
		}
	};

	useEffect(() => {
		if (chatMessages.length > 0 || isChatThinking) {
			if (isNearBottomRef.current || isChatThinking) {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						scrollInsightsThread();
					});
				});
			}
		}
	}, [chatMessages, isChatThinking]);

	// Build chat context
	const buildChatContext = (
		csvData: string[] | undefined,
		insightsActions: InsightsActionsResponse | undefined
	): string => {
		const parts: string[] = [];

		if (name) {
			parts.push(`Chart: ${name}`);
		}

		if (chartMetaDetails) {
			parts.push(`Chart Description: ${chartMetaDetails}`);
		}

		const req = insightsActionsRequest;
		if (req?.metadata) {
			const measures = req.metadata.key_measures.map((m) => m.name).join(', ');
			const dims = req.metadata.dimensions.map((d) => d.name).join(', ');
			if (measures) parts.push(`Measures: ${measures}`);
			if (dims) parts.push(`Dimensions: ${dims}`);

			const { start_date, end_date } = req.metadata.time_range;
			if (start_date && end_date) {
				parts.push(`Time Range: ${start_date} to ${end_date}`);
			}

			if (req.metadata.filters_applied.length > 0) {
				parts.push(`Filters: ${req.metadata.filters_applied.join('; ')}`);
			}
		}

		if (csvData && csvData.length > 0) {
			const headers = csvData[0] || '';
			const sampleRows = csvData.slice(1, 6).join('\n');
			parts.push(`Dataset Headers: ${headers}`);
			parts.push(`Sample Data:\n${sampleRows}`);
		}

		if (insightsActions?.insights?.length) {
			parts.push(`Generated Insights:\n- ${insightsActions.insights.join('\n- ')}`);
		}

		if (insightsActions?.actions?.length) {
			parts.push(`Recommended Actions:\n- ${insightsActions.actions.join('\n- ')}`);
		}

		return parts.join('\n\n');
	};

	const chatContext = useMemo(
		() => buildChatContext(previewData?.csvData, insightsData),
		[previewData?.csvData, insightsData, name, chartMetaDetails, insightsActionsRequest]
	);

	// Auto-generate insights
	const autoGenerateInsights = () => {
		if (insightsActionsRequest) {
			setIsInsightsLoading(true);
			setInsightsError(false);

			retryWithBackoff(() => InsightsActionsAPI.GetInsights(insightsActionsRequest), 3, 1000)
				.then((insights) => {
					if (insights.success) {
						if (typeof insights.data === 'string') {
							insights.data = JSON.parse(insights.data) as InsightsActionsResponse;
						}
						setInsightsData(insights.data);
					} else {
						setInsightsError(true);
						addToast(insights.message, AlertVariant.danger);
					}
				})
				.catch((): void => {
					setInsightsError(true);
					addToast('Error fetching insights after retries.', AlertVariant.danger);
				})
				.finally(() => {
					setIsInsightsLoading(false);
				});
		}
	};

	// Auto-generate on mount (when sidebar opens)
	useEffect(() => {
		if (
			!hasAutoGenerated &&
			previewData?.csvData &&
			insightsActionsRequest &&
			!isInsightsDisabled
		) {
			setHasAutoGenerated(true);
			autoGenerateInsights();
		}
	}, []);

	// Fetch discover questions on mount
	const getDiscoverQuestions = () => {
		if (chartId) {
			Question.GetAll(parseInt(chartId))
				.then((questions) => {
					setFetchedQuestions(questions);
				})
				.catch(() => {
					addToast(
						'An error occured while fetching Discover Questions',
						AlertVariant.danger
					);
				});
		}
	};

	useEffect(() => {
		getDiscoverQuestions();
	}, []);

	// Reset state when sidebar closes
	useEffect(() => {
		if (!insightsOpen) {
			setHasAutoGenerated(false);
			setInsightsData(undefined);
			setInsightsError(false);
			setChatMessages([]);
			setIsChatThinking(false);
		}
	}, [insightsOpen]);

	// Chart meta details handlers
	const closeChartMetadetailsModal = () => {
		setIsChartMetaDetailsModalOpen(false);
	};

	const saveChartMetaDetails = () => {
		const request: TChartMetaDetails = {
			chartId: chartId ? parseInt(chartId) : 0,
			metadetails: chartMetaDetails ?? '',
		};

		Chart.SaveChartMetaDetails(request)
			.then(() => {
				addToast('Chart metadata details saved successfully.', AlertVariant.success);
				setIsChartMetaDetailsModalOpen(false);
			})
			.catch(() => {
				addToast(
					'An error occurred while trying to save chart metadetails. Please try again later.',
					AlertVariant.danger
				);
			});
	};

	const handleRefresh = () => {
		setInsightsData(undefined);
		setHasAutoGenerated(false);
		setChatMessages([]);
		setIsChatThinking(false);
		setInsightsError(false);
		setHasInteracted(false);
		setRefreshKey((k) => k + 1);
		autoGenerateInsights();
	};

	return (
		<div
			className="insights-sidebar-redesign"
			ref={sidebarContainerRef}
			role="complementary"
			aria-label="AI Insights"
		>
			{/* Sticky header */}
			<div className="insights-sticky-header">
				<div className="insights-sticky-header__left">
					<FontAwesomeIcon
						icon={faSparkles}
						color="#0073c3"
					/>
					<span className="insights-sticky-header__title">AI * Insights</span>
				</div>
				<div className="insights-sticky-header__right">
					<Tooltip content="Refresh Insights">
						<Button
							variant="plain"
							className="insights-header-btn"
							onClick={handleRefresh}
						>
							<FontAwesomeIcon icon={faArrowsRotate} />
						</Button>
					</Tooltip>
					<Tooltip content="Chart Metadata">
						<Button
							variant="plain"
							className="insights-header-btn"
							onClick={() => setIsChartMetaDetailsModalOpen(true)}
						>
							<FontAwesomeIcon icon={faGear} />
						</Button>
					</Tooltip>
					{hasViewQuestionPerm && (
						<Tooltip content="Discover Questions">
							<Button
								variant="plain"
								className="insights-header-btn"
								onClick={() => setIsDiscoverQuestionsModalOpen(true)}
							>
								<FontAwesomeIcon icon={faCompass} />
							</Button>
						</Tooltip>
					)}
					<Tooltip content="Expand">
						<Button
							variant="plain"
							className="insights-header-btn"
							onClick={() => setIsInsightsModalOpen(true)}
						>
							<FontAwesomeIcon icon={faExpand} />
						</Button>
					</Tooltip>
					<Tooltip content="Close">
						<Button
							variant="plain"
							className="insights-header-btn"
							onClick={() => {
								setInsightsOpen && setInsightsOpen(false);
							}}
						>
							<FontAwesomeIcon icon={faTimes} />
						</Button>
					</Tooltip>
				</div>
			</div>

			{/* Scrollable thread */}
			<div
				className="insights-thread"
				ref={insightsThreadRef}
				tabIndex={0}
			>
				{!isInsightsDisabled && (
					<>
						{/* Loading skeletons */}
						{isInsightsLoading && !insightsData && (
							<div className="insights-loading">
								<Skeleton />
								<span className="spacer-xxs"></span>
								<Skeleton />
								<span className="spacer-xxs"></span>
								<Skeleton />
								<span className="spacer-xxs"></span>
								<Skeleton width="60%" />
							</div>
						)}

						{/* Error state with retry */}
						{insightsError && !insightsData && !isInsightsLoading && (
							<div style={{ padding: '1rem', textAlign: 'center' }}>
								<p style={{ color: '#6a6e73', marginBottom: '0.5rem' }}>
									Failed to generate insights.
								</p>
								<Button
									variant="link"
									onClick={() => {
										setInsightsError(false);
										autoGenerateInsights();
									}}
								>
									Try again
								</Button>
							</div>
						)}

						{/* Insights & Actions Card */}
						{insightsData && (
							<InsightsActions
								insights={insightsData}
								hasBackground={false}
								collapsible={false}
							/>
						)}

						{/* Chat messages in the shared thread */}
						<div className="insights-chat-thread">
							<ChatMessages
								messages={chatMessages}
								isThinking={isChatThinking}
							/>
						</div>
					</>
				)}

				{isInsightsDisabled && (
					<div className="insights-disabled-msg">
						AI Insights are disabled for this zone.
					</div>
				)}
			</div>

			{/* Pinned chat input at bottom */}
			{!isInsightsDisabled && (
				<div className="insights-pinned-input">
					{chatMessages.length === 0 &&
						!isChatThinking &&
						!hasInteracted &&
						insightsData && (
							<small className="insights-chat-prompt">Ask a follow-up question</small>
						)}
					<ChatInterface
						context={chatContext}
						contextId={refreshKey}
						externalThread={true}
						onMessagesChange={setChatMessages}
						onThinkingChange={setIsChatThinking}
						showModal={isInsightsModalOpen}
						onModalClose={() => setIsInsightsModalOpen(false)}
						onFirstInteraction={() => setHasInteracted(true)}
						modalHeaderContent={
							insightsData ? (
								<InsightsActions
									insights={insightsData}
									hasBackground={false}
									collapsible={false}
								/>
							) : undefined
						}
						disabled={!insightsData && !insightsError}
					/>
				</div>
			)}

			{/* Chart Metadata Modal */}
			<ChartMetaDataModal
				isOpen={isChartMetaDetailsModalOpen}
				onClose={closeChartMetadetailsModal}
				onSubmit={() => {
					saveChartMetaDetails();
				}}
				onTextChange={(value) => {
					setChartMetaDetails(value);
				}}
				titleText={`Meta-details for Chart`}
				textValue={chartMetaDetails}
				textLabel={
					<>
						<p>
							Here you can configure additional context messages to be passed along
							for when AI insights are generated for the <b>{name}</b> chart
						</p>
					</>
				}
			/>

			{/* Discover Questions Modal */}
			<DiscoverQuestionsModal
				isOpen={isDiscoverQuestionsModalOpen}
				onClose={() => setIsDiscoverQuestionsModalOpen(false)}
				chartId={chartId ? parseInt(chartId) : undefined}
			/>
		</div>
	);
};

export default InsightsChartSidebar;
